import { z } from 'zod'

/**
 * Estoque de insumos e PDV das barracas (RF03.4).
 *
 * Quantidade e decimal com 3 casas porque insumo se compra fracionado - 2,5 kg
 * de carne, 1,5 L de refrigerante. Dinheiro continua inteiro em centavos.
 */

export const stockUnitSchema = z.enum(['UN', 'KG', 'G', 'L', 'ML', 'CX', 'PCT', 'DZ'])
export type StockUnit = z.infer<typeof stockUnitSchema>

export const STOCK_UNIT_LABELS: Record<StockUnit, string> = {
  UN: 'unidade',
  KG: 'quilo',
  G: 'grama',
  L: 'litro',
  ML: 'mililitro',
  CX: 'caixa',
  PCT: 'pacote',
  DZ: 'duzia',
}

export const STOCK_UNIT_SHORT: Record<StockUnit, string> = {
  UN: 'un',
  KG: 'kg',
  G: 'g',
  L: 'L',
  ML: 'ml',
  CX: 'cx',
  PCT: 'pct',
  DZ: 'dz',
}

/** Quantidade com no maximo 3 casas decimais. Aceita virgula do teclado pt-BR. */
export const quantitySchema = z
  .union([z.number(), z.string()])
  .transform((value) => (typeof value === 'number' ? value : Number(value.replace(',', '.'))))
  .refine((value) => Number.isFinite(value), { error: 'Informe uma quantidade valida' })
  .refine((value) => Math.abs(value * 1000 - Math.round(value * 1000)) < 1e-6, {
    error: 'Use no maximo 3 casas decimais',
  })

const stockItemShape = {
  name: z.string().min(2, 'Informe o nome do item').max(120).trim(),
  category: z.string().max(80).trim().optional(),
  unit: stockUnitSchema.default('UN'),
  minQuantity: quantitySchema.refine((v) => v >= 0, 'Nao pode ser negativa').nullable().optional(),
  /** Item vendido diretamente no PDV. */
  isForSale: z.boolean().default(false),
  salePriceCents: z.coerce.number().int().min(0).nullable().optional(),
}

/** Invariante do item a venda: quem vende precisa de preco. */
export function assertSalePrice(
  data: { isForSale?: boolean; salePriceCents?: number | null },
): string | null {
  if (data.isForSale && (data.salePriceCents === null || data.salePriceCents === undefined)) {
    return 'Item a venda precisa de preco'
  }
  return null
}

export const stockItemInputSchema = z.object(stockItemShape).superRefine((data, ctx) => {
  const erro = assertSalePrice(data)
  if (erro) {
    ctx.addIssue({ code: 'custom', path: ['salePriceCents'], message: erro })
  }
})

/**
 * Schema de atualizacao parcial.
 *
 * Construido a partir do shape, e nao com `.partial()` sobre o schema de
 * criacao: o Zod 4 recusa `.partial()` em objeto com refinamento - e o erro
 * aparece so em tempo de execucao, ao registrar a rota.
 *
 * Sem o refinamento de proposito: numa atualizacao parcial, `isForSale: true`
 * pode ser valido porque o item JA tem preco gravado. A invariante e conferida
 * no servidor contra o estado mesclado, via `assertSalePrice`.
 */
export const stockItemUpdateSchema = z.object(stockItemShape).partial()

export type StockItemInput = z.infer<typeof stockItemInputSchema>
export type StockItemUpdate = z.infer<typeof stockItemUpdateSchema>

export const stockMovementTypeSchema = z.enum([
  'ENTRADA',
  'BAIXA_VENDA',
  'PERDA',
  'AJUSTE',
  'DEVOLUCAO',
])
export type StockMovementType = z.infer<typeof stockMovementTypeSchema>

export const STOCK_MOVEMENT_TYPE_LABELS: Record<StockMovementType, string> = {
  ENTRADA: 'Entrada (compra ou doacao)',
  BAIXA_VENDA: 'Baixa por venda',
  PERDA: 'Perda, quebra ou vencimento',
  AJUSTE: 'Ajuste de contagem',
  DEVOLUCAO: 'Devolucao ao fornecedor',
}

/**
 * Movimentacao lancada manualmente.
 *
 * BAIXA_VENDA fica fora: ela e criada pela venda no PDV, dentro da mesma
 * transacao. Permitir lancamento manual desse tipo abriria a porta para o
 * estoque discordar das vendas registradas.
 */
export const manualStockMovementSchema = z.object({
  type: z.enum(['ENTRADA', 'PERDA', 'AJUSTE', 'DEVOLUCAO']),
  /** Sempre positiva; o sinal vem do tipo. Em AJUSTE, e o saldo contado. */
  quantity: quantitySchema.refine((v) => v > 0, 'A quantidade deve ser maior que zero'),
  unitCostCents: z.coerce.number().int().min(0).nullable().optional(),
  note: z.string().max(300).trim().optional(),
})

export type ManualStockMovementInput = z.infer<typeof manualStockMovementSchema>

/** Tipos que somam ao saldo. AJUSTE e tratado a parte: define o saldo. */
export const POSITIVE_MOVEMENT_TYPES: readonly StockMovementType[] = ['ENTRADA']
export const NEGATIVE_MOVEMENT_TYPES: readonly StockMovementType[] = [
  'BAIXA_VENDA',
  'PERDA',
  'DEVOLUCAO',
]

// --- Caixa -----------------------------------------------------------------

export const cashSessionStatusSchema = z.enum(['ABERTA', 'FECHADA'])
export type CashSessionStatus = z.infer<typeof cashSessionStatusSchema>

export const openCashSessionSchema = z.object({
  label: z.string().min(2, 'Identifique o caixa (ex: Barraca do pastel)').max(80).trim(),
  /** Troco inicial em centavos. */
  openingFloatCents: z.coerce.number().int().min(0).default(0),
})

export const closeCashSessionSchema = z.object({
  /** Dinheiro fisico contado no fechamento, em centavos. */
  countedCents: z.coerce.number().int().min(0),
  notes: z.string().max(500).trim().optional(),
})

/** Conciliacao do caixa: o que deveria ter contra o que foi contado. */
export interface CashSessionSummary {
  id: string
  label: string
  status: CashSessionStatus
  openedAt: string
  closedAt: string | null
  openingFloatCents: number
  /** Somente vendas em dinheiro entram na conferencia da gaveta. */
  salesCashCents: number
  salesPixCents: number
  salesCardCents: number
  totalSalesCents: number
  expectedCashCents: number
  countedCents: number | null
  /** Positivo = sobra, negativo = falta. */
  differenceCents: number | null
  saleCount: number
  openedByName: string | null
  closedByName: string | null
  notes: string | null
}

// --- Venda (PDV) -----------------------------------------------------------

export const paymentMethodSchema = z.enum(['DINHEIRO', 'PIX', 'CARTAO'])
export type PaymentMethod = z.infer<typeof paymentMethodSchema>

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  DINHEIRO: 'Dinheiro',
  PIX: 'PIX',
  CARTAO: 'Cartao',
}

export const saleItemInputSchema = z.object({
  stockItemId: z.string().min(1),
  quantity: quantitySchema.refine((v) => v > 0, 'Quantidade deve ser maior que zero'),
})

export const saleInputSchema = z
  .object({
    cashSessionId: z.string().min(1, 'Abra um caixa antes de vender'),
    items: z.array(saleItemInputSchema).min(1, 'Adicione ao menos um item'),
    paymentMethod: paymentMethodSchema.default('DINHEIRO'),
    /** Valor recebido em dinheiro, para calcular o troco. */
    receivedCents: z.coerce.number().int().min(0).nullable().optional(),
    note: z.string().max(200).trim().optional(),
    /**
     * Chave de idempotencia gerada pelo PDV.
     *
     * Existe por causa da rede de festa de paroquia: a venda e registrada no
     * navegador, a conexao cai, e o reenvio depois nao pode gravar a mesma
     * venda duas vezes - o que inflaria o caixa e derrubaria o estoque em
     * dobro.
     */
    clientRef: z.string().min(8, 'Referencia invalida').max(64),
  })
  .superRefine((data, ctx) => {
    const ids = data.items.map((item) => item.stockItemId)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['items'],
        message: 'Item repetido na venda. Some a quantidade em uma unica linha.',
      })
    }
  })

export type SaleInput = z.infer<typeof saleInputSchema>

export interface SaleReceipt {
  id: string
  number: number
  totalCents: number
  paymentMethod: PaymentMethod
  changeCents: number | null
  soldAt: string
  items: { itemName: string; quantity: number; unit: StockUnit; unitPriceCents: number; totalCents: number }[]
}

/** Calcula o total de uma venda a partir dos precos vigentes. */
export function calculateSaleTotal(
  items: readonly { quantity: number; unitPriceCents: number }[],
): number {
  // Arredonda por linha, nao no total: e assim que o troco confere com a
  // soma que o voluntario faz de cabeca na barraca.
  return items.reduce((sum, item) => sum + Math.round(item.quantity * item.unitPriceCents), 0)
}
